// The role ladder, the tabs built from it, and the resolver the router asks on every navigation.
//
// Whether an account is yours is not decided here: the server checks branch membership on every
// request carrying one, and it is the only thing protecting the data. What it never checks is
// which roles you hold, so that is what this side judges.

// Mirrors DotNet/src/Amplify.Identity.Domain/NextLevelIra/NextLevelRoleLadder.cs. A membership is
// anchored at one account type and reaches a contiguous run of tiers below it, reported on the
// wire as (accountTypeCode, maxAccountReach).
//
// Order matters: index position IS the tier depth. Anything not in this list is off the ladder.
const TIER_ORDER = ["registeredInvestmentAdvisor", "advisoryFirm", "advisor"];

function tierIndexFor(accountTypeCode) {
  const index = TIER_ORDER.indexOf(accountTypeCode);
  return index === -1 ? null : index;
}

// The ordered tiers an anchor reaches, inclusive of both ends. Nonsense input answers with an
// empty list rather than null, so no caller has to special-case it.
export function tiersFrom(anchorAccountTypeCode, maxAccountReach) {
  const anchorIndex = tierIndexFor(anchorAccountTypeCode);
  const reachIndex = tierIndexFor(maxAccountReach);

  if (anchorIndex === null || reachIndex === null || reachIndex < anchorIndex) {
    return [];
  }

  return TIER_ORDER.slice(anchorIndex, reachIndex + 1);
}

// The tabs are the tiers the user holds a role at, plus Reports. Roles rather than permissions:
// an RIA Admin can manage firms without holding a Firm role, and gets no Firm tab.
const TIER_LABELS = {
  registeredInvestmentAdvisor: "RIA functions",
  advisoryFirm: "Advisory firm functions",
  advisor: "Advisor functions",
};

// The anchor tier is absent on purpose: you can always return to your own account, so it needs no
// picker.
const SELECTION_ROUTE_BY_TIER = {
  advisoryFirm: "/select-advisory-firm",
  advisor: "/select-advisor",
};

const REPORTS_PATH_BY_TIER = {
  registeredInvestmentAdvisor: "ria-reports",
  advisoryFirm: "advisory-firm-reports",
  advisor: "advisor-reports",
};

const REPORTS_TAB = "reports";

function selectionRouteFor(tier) {
  return SELECTION_ROUTE_BY_TIER[tier] || null;
}

// Read back from the map that sends the tab here, rather than declared on the route: the map has
// to be right for the tab to work at all, so it cannot fall out of step the way an annotation
// could.
export function selectionRouteTier(path) {
  if (!path) return null;

  const match = Object.keys(SELECTION_ROUTE_BY_TIER).find(
    (tier) => SELECTION_ROUTE_BY_TIER[tier] === path
  );

  return match || null;
}

function isReportsPath(path) {
  return Object.values(REPORTS_PATH_BY_TIER).some((suffix) =>
    path.endsWith(`/${suffix}`)
  );
}

// Everything unrecognised is the tier of the account you are in.
function activeTabFor(route, currentAccount) {
  const paths = route ? route.matched.map((record) => record.path) : [];

  // The reports pages sit under a tier's account but belong to the Reports tab.
  if (paths.some(isReportsPath)) return REPORTS_TAB;

  // A selection step carries no account, so the previous tier would otherwise stay highlighted
  // while you choose the next one.
  const selecting = paths.map(selectionRouteTier).find(Boolean);
  if (selecting) return selecting;

  return currentAccount ? currentAccount.accountTypeCode : null;
}

// Whether the top bar is shown at all, which also decides where Reports and User settings live:
// with a bar they move into it, without one they stay in the left navigation.
export function hasContextTabs(anchorAccount) {
  if (!anchorAccount) return false;

  return (
    tiersFrom(anchorAccount.accountTypeCode, anchorAccount.maxAccountReach)
      .length >= 2
  );
}

// Which of the two account controls the left navigation shows: at the anchor the existing
// switcher, built on the user's own memberships, and below it the tier selector, which those
// memberships cannot describe.
export function isBelowAnchor(anchorAccount, currentAccount) {
  if (!hasContextTabs(anchorAccount) || !currentAccount) return false;

  return currentAccount.id !== anchorAccount.uuid;
}

// Whether the user may operate AS an account -- about the ACCOUNT's tier, not the route's. A route
// can only declare which tiers may be there, and the dashboard serves all three; which tier an
// account IS is knowable only once it is loaded. This is the half the resolver below cannot ask.
//
// Permissive without an anchor, for the same reason the resolver is.
export function holdsTier(anchorAccount, accountTypeCode) {
  if (!anchorAccount) return true;

  return tiersFrom(
    anchorAccount.accountTypeCode,
    anchorAccount.maxAccountReach
  ).includes(accountTypeCode);
}

// The tabs to render, or an empty list where there is nothing to switch between.
export function buildContextTabs(
  anchorAccount,
  currentAccount,
  route,
  selectedAccountUuidByTier = {}
) {
  if (!anchorAccount) return [];

  if (!hasContextTabs(anchorAccount)) return [];

  const tiers = tiersFrom(
    anchorAccount.accountTypeCode,
    anchorAccount.maxAccountReach
  );

  const activeTab = activeTabFor(route, currentAccount);
  const isAnchorTier = (tier) => tier === anchorAccount.accountTypeCode;
  const inTier = (tier) =>
    currentAccount && currentAccount.accountTypeCode === tier;

  // Choosing is asked once per tier and then held, so a tab resumes where you left it and the
  // other tiers' choices are untouched.
  const rememberedIn = (tier) =>
    selectedAccountUuidByTier[tier]
      ? `/accounts/${selectedAccountUuidByTier[tier]}`
      : null;

  const tabs = tiers.map((tier) => ({
    key: tier,
    label: TIER_LABELS[tier],
    // Already in that tier: stay where you are. Otherwise the anchor needs no choosing, and a tier
    // below it resumes its last account or sends you to pick one.
    to: inTier(tier)
      ? `/accounts/${currentAccount.id}`
      : isAnchorTier(tier)
      ? `/accounts/${anchorAccount.uuid}`
      : rememberedIn(tier) || selectionRouteFor(tier),
    active: activeTab === tier,
  }));

  const reportsTier =
    currentAccount && REPORTS_PATH_BY_TIER[currentAccount.accountTypeCode]
      ? currentAccount.accountTypeCode
      : anchorAccount.accountTypeCode;
  const reportsAccountUuid =
    currentAccount && currentAccount.id
      ? currentAccount.id
      : anchorAccount.uuid;

  tabs.push({
    key: REPORTS_TAB,
    label: "Reports",
    to: `/accounts/${reportsAccountUuid}/${REPORTS_PATH_BY_TIER[reportsTier]}`,
    active: activeTab === REPORTS_TAB,
  });

  return tabs;
}

// Which account a route puts you in, and whether you may be there. Answered synchronously, from
// the route and what is already in the store; deciding here means a refusal happens before the
// page mounts rather than as a failed fetch afterwards.

// Not every route carrying an accountUuid puts you IN that account. `/accounts/{uuid}/...` does.
// `/advisory-firms/{uuid}`, `/advisors/{uuid}` and `/firm-advisors/{uuid}` do not: they are a
// parent looking at a child's record from its own context, which is why an RIA can open one of its
// firms without holding a Firm role.
function isContextSettingRoute(to) {
  return to.matched.some(
    (record) => record.path && record.path.startsWith("/accounts/:accountUuid")
  );
}

// The tiers a route declares: the user must hold one of them to be here at all. Which tier the
// account named in the route actually is cannot be asked here -- holdsTier above does that, once
// the account is loaded.
function routeTiers(to) {
  return (
    to.matched
      .map((record) => record.meta && record.meta.tiers)
      .find(Boolean) || []
  );
}

export function resolveAccountContext(to, anchorAccount) {
  // Permissive on purpose: the anchor comes from the authenticated-user payload, so an API that
  // does not report it yet would otherwise lock every user out of every route.
  if (!anchorAccount) {
    return { allowed: true, contextAccountUuid: null };
  }

  const tiers = tiersFrom(
    anchorAccount.accountTypeCode,
    anchorAccount.maxAccountReach
  );

  // Whether the account is yours is the server's to answer. What it does not check is which roles
  // you hold -- reaching a firm's advisors is not the same as being allowed to operate as one --
  // so that is what stays here.
  const selectionTier = to.matched
    .map((record) => selectionRouteTier(record.path))
    .find(Boolean);

  if (selectionTier && !tiers.includes(selectionTier)) {
    return { allowed: false, contextAccountUuid: null };
  }

  const pageTiers = routeTiers(to);

  if (pageTiers.length && !pageTiers.some((tier) => tiers.includes(tier))) {
    return { allowed: false, contextAccountUuid: null };
  }

  const accountUuid = (to.params && to.params.accountUuid) || null;

  // Routes with no account, and routes that open a child's record from your own context, both
  // leave the context where it was.
  if (!accountUuid || !isContextSettingRoute(to)) {
    return { allowed: true, contextAccountUuid: null };
  }

  return { allowed: true, contextAccountUuid: accountUuid };
}
